import React, { useEffect, useRef, useState } from "react";
import { closeModal, openModal } from "../../../assets/Utils/modal";
import IconNew from "../../../assets/TakyonDesignSystem/components/Icon/Icon";
import { fireTagManagerEvent } from "../../../assets/Services/tagmanager";
import { ANALYTICS_DETAILS_SECTION } from "../../../config/analytics";
import { selectUser } from "../../../redux/slices/userSlice";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

interface HotelDescriptionProps {
  html: string;
  maxHeight?: number; // in px
}

const HotelDescription: React.FC<HotelDescriptionProps> = ({
  html,
  maxHeight = 100,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const user = useSelector(selectUser);
  const { t } = useTranslation();

  useEffect(() => {
    const el = containerRef.current;
    if (el) {
      setIsOverflowing(el.scrollHeight > el.clientHeight);
    }
  }, [html, maxHeight]);

  return (
    <>
      <div className="relative">
        <div
          ref={containerRef}
          className="overflow-hidden mt-5"
          style={{ maxHeight }}
          dangerouslySetInnerHTML={{ __html: html }}
        />

        {/* Fade overlay if overflowing */}
        {isOverflowing ? (
          <div className="absolute bottom-0 left-0 w-full h-12 bg-gradient-to-t from-white to-transparent pointer-events-none" />
        ) : null}
      </div>
      {isOverflowing ? (
        <div className="w-full flex justify-center mt-6">
          <button
            type="button"
            className="bg-[#F3F3F3] text-sm font-medium rounded-xl px-3.5 h-[2.375rem] flex items-center justify-center border-none text-black"
            onClick={() => {
              openModal(
                {
                  content: (
                    <div className="flex flex-col">
                      <div className="relative flex justify-end px-3 items-center box-border border-bottom h-12">
                        <h3 className="text-sm font-medium text-center mb-0 absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2">
                          {t("ds.description")}
                        </h3>
                        <button
                          type="button"
                          onClick={closeModal}
                          className="bg-transparent border-none touch-hitbox relative"
                        >
                          <IconNew icon="close" className="size-5" />
                        </button>
                      </div>
                      <div className="flex flex-col overflow-y-auto h-[calc(100dvh-3rem)] lg:h-[46.75rem] px-4 py-3">
                        <p
                          className="text-sm font-medium px-4 py-3"
                          dangerouslySetInnerHTML={{ __html: html }}
                        />
                      </div>
                    </div>
                  ),
                  className:
                    "rounded-t-3xl self-end h-[calc(100dvh-1rem)] lg:h-auto lg:self-center lg:rounded-3xl",
                },
                { hideModalCloseBtn: true },
              );
              fireTagManagerEvent("open_details_section", {
                detail_section: ANALYTICS_DETAILS_SECTION.DESCRIPTION,
                user_id: user?._id ?? null,
              });
            }}
          >
            {t("ds.read_more")}
          </button>
        </div>
      ) : null}
    </>
  );
};

export default HotelDescription;
